import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { useStreaks } from "../features/streaks/useStreaks";
import { useLastSwing } from "../hooks/useLastSwing";
import { Flame, Play, Target, ArrowRight } from "lucide-react";
import { fetchProfile } from "@/services/profile-service";
import { getAvailableSwings } from "@/lib/api/snapshot";
import { getLatestTrainingPlan } from "@/lib/api/training-program";

export default function HomeScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPlatformAdmin } = useAdminAccess();
  const { streak, isLoading } = useStreaks();
  const { data: lastSwing, isLoading: lastSwingLoading } = useLastSwing();
  const [profile, setProfile] = useState<any>(null);
  const [availableSwingCount, setAvailableSwingCount] = useState<number | null>(null);
  const [hasTrainingPlan, setHasTrainingPlan] = useState<boolean | null>(null);
  const [loadingTrainingPlan, setLoadingTrainingPlan] = useState(true);

  // Fetch profile to get nickname and check for current drill
  useEffect(() => {
    if (user) {
      fetchProfile(user.id).then((data) => {
        if (data) {
          setProfile(data);
        }
      });

      // Check available swings for training plan
      getAvailableSwings()
        .then((swings) => {
          setAvailableSwingCount(swings.length);
        })
        .catch((error) => {
          console.error('Error loading available swings:', error);
          setAvailableSwingCount(0);
        });

      // Check if user has an active training plan
      getLatestTrainingPlan()
        .then((plan) => {
          setHasTrainingPlan(plan !== null);
        })
        .catch((error) => {
          console.error('Error loading training plan:', error);
          setHasTrainingPlan(false);
        })
        .finally(() => {
          setLoadingTrainingPlan(false);
        });
    }
  }, [user]);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  // Use nickname (display name) if available, fallback to first_name, then email, then "Golfer"
  const userName = profile?.nickname || profile?.first_name || user?.email?.split("@")[0] || "Golfer";

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-6 py-4 flex items-center justify-between h-20 shadow-sm">
        <img 
          src="/nodoubles_logo_trans.png" 
          alt="No Doubles Golf Co" 
          className="h-12 object-contain"
        />
        <div className="flex items-center gap-3">
          {isPlatformAdmin && (
            <button
              onClick={() => navigate("/demo")}
              className="px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-sm font-semibold transition-all duration-200 hover:bg-white/25 active:scale-95"
            >
              Demo
            </button>
          )}
          <button 
            onClick={() => navigate("/mvp/profile")}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] flex items-center justify-center text-[#0D382A] transition-all duration-200 hover:shadow-lg"
          >
            {userName.charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <h2 className="text-[#0D382A] text-2xl sm:text-3xl">
            {greeting()}, {userName}
          </h2>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md">
            <div className="relative">
              <Flame className="w-5 h-5 text-[#F9C94E]" fill="#F9C94E" />
              <div className="absolute inset-0 bg-[#F9C94E]/30 blur-md rounded-full" />
            </div>
            <span className="text-[#0D382A]">{streak}-day streak</span>
          </div>
        </div>

        {/* Last Swing Tile */}
        {lastSwingLoading ? (
          <div className="mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
                <div className="h-48 bg-gray-200 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        ) : lastSwing ? (
          <div className="mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
              <h3 className="text-[#0D382A] text-xl mb-4">Your Last Swing</h3>
              
              <div 
                className="relative rounded-xl overflow-hidden mb-4 bg-gray-900 aspect-video group cursor-pointer"
                onClick={() => navigate(`/mvp/analysis/${lastSwing.id}`)}
              >
                {/* Video thumbnail */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                    <Play className="w-8 h-8 text-[#0D382A] ml-1" fill="#0D382A" />
                  </div>
                </div>
                {lastSwing.cf_playback_url ? (
                  <video
                    src={lastSwing.cf_playback_url}
                    className="w-full h-full object-cover opacity-70"
                    muted
                    playsInline
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#0D382A] to-[#196644] opacity-70" />
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-500 text-sm">
                  {lastSwing.created_at
                    ? new Date(lastSwing.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: new Date(lastSwing.created_at).getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
                      })
                    : "Recently analyzed"}
                </p>
                <button
                  onClick={() => navigate(`/mvp/analysis/${lastSwing.id}`)}
                  className="px-6 py-2 border-2 border-[#0D382A] text-[#0D382A] rounded-full transition-all duration-200 hover:bg-[#0D382A]/5"
                >
                  View Analysis
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
              <h3 className="text-[#0D382A] text-xl mb-4">Your Last Swing</h3>
              <p className="text-gray-600 text-center py-8">
                No swings yet. Upload your first swing to get started!
              </p>
              <button
                onClick={() => navigate("/mvp/upload")}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105"
              >
                Upload Your First Swing
              </button>
            </div>
          </div>
        )}

        {/* Training Plan Tile */}
        <div className="mb-6">
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)] transition-all duration-300 hover:shadow-[0_4px_16px_rgba(13,56,42,0.12)]">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#0D382A]" />
                </div>
                <div>
                  <h3 className="text-[#0D382A] text-xl mb-1">Training Plan</h3>
                  <p className="text-gray-600 text-sm">
                    {loadingTrainingPlan ? (
                      "Loading..."
                    ) : hasTrainingPlan ? (
                      "Continue your personalized training plan"
                    ) : availableSwingCount !== null && availableSwingCount >= 5 ? (
                      "Create a personalized training plan from your swings"
                    ) : (
                      `Upload ${availableSwingCount !== null ? Math.max(0, 5 - availableSwingCount) : 5} more ${availableSwingCount !== null && availableSwingCount === 4 ? 'swing' : 'swings'} to get started`
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            {loadingTrainingPlan ? (
              <button
                disabled
                className="w-full px-6 py-3 bg-gray-200 text-gray-500 rounded-full"
              >
                Loading...
              </button>
            ) : hasTrainingPlan ? (
              <button
                onClick={() => navigate("/training-program")}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105 flex items-center justify-center gap-2"
              >
                Continue Training Plan
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : availableSwingCount !== null && availableSwingCount >= 5 ? (
              <button
                onClick={() => navigate("/snapshot/select")}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105 flex items-center justify-center gap-2"
              >
                Create Training Plan
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => navigate("/mvp/upload")}
                className="w-full px-6 py-3 border-2 border-[#0D382A] text-[#0D382A] rounded-full transition-all duration-200 hover:bg-[#0D382A]/5"
              >
                Upload More Swings
              </button>
            )}
          </div>
        </div>

        {/* Quick Tip Tile */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
          <h3 className="text-[#0D382A] text-xl mb-3">Quick Tip</h3>
          <p className="text-gray-600">
            Consistency beats perfection. Focus on making small improvements every day.
          </p>
        </div>
      </main>
    </div>
  );
}
